'use client'

import { useState } from 'react'
import Image from 'next/image'
import { Mic, PhoneOff, Sparkles } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { CallTimer } from '@/components/call-timer'
import { CHARACTER_THEMES, type CharacterThemeKey } from '@/lib/character-themes'
import { useDeferredHeroMedia } from '@/hooks/use-deferred-hero-media'
import type { CharacterImageSources } from '@/lib/characters'

/**
 * Simulación de videollamada del hero, parametrizada por personaje y tema.
 *
 * Las landings clásicas tienen una copia casi idéntica cada una
 * (`reyes-hero-video-mock`, `hada-hero-video-mock`, `raton-perez-hero-video-mock`):
 * este componente es la versión genérica y está pensado para absorberlas cuando
 * se migren. Aquí lo usan las landings del catálogo, que comparten estructura y
 * solo cambian tema, retrato y etiquetas.
 *
 * Los textos de chrome (estado de llamada, "Tú") salen del namespace `landing`,
 * que ya los tiene y es común a todas las superficies públicas.
 */
export function GenericHeroVideoMock({
  name,
  location,
  imageSources,
  videoSrc,
  themeKey,
}: {
  name: string
  /** Línea bajo el nombre: de dónde llama el personaje. */
  location: string
  imageSources: CharacterImageSources
  /** Sin clip, el mock se queda en el retrato con el chrome de llamada. */
  videoSrc?: string
  themeKey: CharacterThemeKey
}) {
  const t = useTranslations('landing')
  const { containerRef, isActive } = useDeferredHeroMedia()
  const [mainVideoLoaded, setMainVideoLoaded] = useState(false)
  const [pipVideoLoaded, setPipVideoLoaded] = useState(false)
  const theme = CHARACTER_THEMES[themeKey]
  const showVideo = isActive && videoSrc !== undefined

  return (
    <div className="relative" ref={containerRef} style={{ contain: 'layout style paint' }}>
      <div className={`absolute inset-0 rounded-3xl ${theme.videoCall.glow} blur-2xl`} />
      <div
        className={`relative overflow-hidden rounded-3xl border-2 border-white/15 ${theme.heroBg} shadow-2xl`}
      >
        <div className={`relative aspect-square w-full ${theme.heroBg}`} style={{ minHeight: '1px' }}>
          <picture className="absolute inset-0">
            <source srcSet={imageSources.webp} type="image/webp" />
            {/* Retrato ya optimizado; mismo enfoque que los mocks clásicos. */}
            <img
              src={imageSources.src}
              alt={name}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className={`h-full w-full object-cover object-top transition-opacity duration-300 ${
                showVideo && mainVideoLoaded ? 'opacity-0' : 'opacity-100'
              }`}
              style={{ willChange: 'opacity' }}
            />
          </picture>

          {showVideo && (
            <video
              autoPlay
              loop
              muted
              playsInline
              poster={imageSources.webp}
              preload="none"
              aria-label={name}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
                mainVideoLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ willChange: 'opacity' }}
              onLoadedData={() => setMainVideoLoaded(true)}
              onCanPlay={() => setMainVideoLoaded(true)}
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
          )}

          <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 backdrop-blur-sm">
            <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-emerald-400" />
            <span className="text-sm font-medium text-white">{t('hero.callStatus')}</span>
            <CallTimer initialSeconds={165} className="text-sm text-white/80" />
          </div>

          <div className="absolute bottom-4 left-3 rounded-lg bg-black/60 px-3 py-2 backdrop-blur-sm">
            <p className="text-sm font-bold text-white">{name}</p>
            <p className="text-xs text-white/70">{location}</p>
          </div>

          <div
            className={`absolute right-3 top-3 h-24 w-[72px] overflow-hidden rounded-2xl border-2 bg-black shadow-xl md:h-32 md:w-24 ${theme.videoCall.softBorder}`}
          >
            <Image
              src="/videos/child-pip-poster.webp"
              alt={t('hero.youLabel')}
              fill
              sizes="96px"
              className={`scale-x-[-1] object-cover transition-opacity duration-300 ${
                isActive && pipVideoLoaded ? 'opacity-0' : 'opacity-100'
              }`}
              loading="lazy"
            />
            {isActive && (
              <video
                autoPlay
                loop
                muted
                playsInline
                poster="/videos/child-pip-poster.webp"
                preload="none"
                className={`absolute inset-0 h-full w-full scale-x-[-1] object-cover transition-opacity duration-300 ${
                  pipVideoLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoadedData={() => setPipVideoLoaded(true)}
                onCanPlay={() => setPipVideoLoaded(true)}
              >
                <source src="/videos/child-pip.mp4" type="video/mp4" />
              </video>
            )}
            <div className="absolute bottom-1 left-1/2 z-10 -translate-x-1/2 rounded bg-black/70 px-2 py-0.5 text-[10px] text-white backdrop-blur-sm">
              {t('hero.youLabel')}
            </div>
          </div>

          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/20 shadow-lg backdrop-blur-sm md:h-12 md:w-12">
              <Mic className="h-5 w-5 text-white" />
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500 shadow-lg md:h-14 md:w-14">
              <PhoneOff className="h-5 w-5 text-white md:h-6 md:w-6" />
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/20 shadow-lg backdrop-blur-sm md:h-12 md:w-12">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
